import { useLocation, useNavigate } from 'react-router-dom';
import { GoogleLoginButton } from '../components/auth/GoogleLoginButton';

/**
 * Login page component
 *
 * @description Provides the main authentication interface for users.
 * Features a clean, centered design with Google OAuth authentication.
 * Redirects users to their intended destination after successful login.
 *
 * Features:
 * - Responsive design that works on all screen sizes
 * - Dark mode support
 * - Redirect to intended page after login
 * - Error handling and user feedback
 * - Professional branding and messaging
 *
 * @example
 * ```tsx
 * // Used in router configuration
 * <Route path="/login" element={<LoginPage />} />
 * ```
 */
export function LoginPage() {
	const navigate = useNavigate();
	const location = useLocation();

	/**
	 * Get the intended redirect path after login
	 */
	const getRedirectPath = (): string => {
		const params = new URLSearchParams(location.search);
		return params.get('redirect') || '/flows';
	};

	/**
	 * Handle successful authentication
	 */
	const handleLoginSuccess = () => {
		const redirectPath = getRedirectPath();
		navigate(redirectPath, { replace: true });
	};

	/**
	 * Handle authentication error
	 */
	const handleLoginError = (error: string) => {
		console.error('Login error:', error);
		// Error is already displayed by GoogleLoginButton component
	};

	return (
		<div className="flex justify-center items-center px-4 py-12 min-h-screen bg-gray-50 dark:bg-gray-900 sm:px-6 lg:px-8">
			<div className="space-y-8 w-full max-w-md">
				{/* Header */}
				<div className="text-center">
					{/* Company Logo */}
					<div className="flex justify-center items-center mx-auto w-20 h-20">
						<img src="/logo.svg" alt="Company Logo" className="w-20 h-20" />
					</div>

					<h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
						Welcome to Composer
					</h2>

					<p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
						Sign in to your account to manage workflows
					</p>
				</div>

				{/* Login form */}
				<div className="mt-8 space-y-6">
					<div className="px-6 py-8 bg-white rounded-lg border border-gray-200 shadow-lg dark:bg-gray-800 dark:border-gray-700">
						<div className="space-y-6">
							{/* Welcome message */}
							<div className="text-center">
								<h3 className="text-lg font-medium text-gray-900 dark:text-white">
									Authentication Required
								</h3>
								<p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
									Please sign in with your Google account to continue
								</p>
							</div>

							{/* Google login button */}
							<GoogleLoginButton
								onSuccess={handleLoginSuccess}
								onError={handleLoginError}
								text="Sign in with Google"
							/>

							{/* Additional info */}
							<div className="text-center">
								<p className="text-xs text-gray-500 dark:text-gray-400">
									By signing in, you agree to our terms of service and privacy
									policy
								</p>
							</div>
						</div>
					</div>

					{/* Help text */}
					<div className="text-center">
						<p className="text-sm text-gray-600 dark:text-gray-400">
							Having trouble signing in?{' '}
							<a
								href="mailto:support@vquip.com"
								className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
							>
								Contact support
							</a>
						</p>
					</div>
				</div>
			</div>
		</div>
	);
}
